import { createAction } from '@reduxjs/toolkit';

export interface Permission {
    id: number;
    title: string;
}

export interface Team {
    id: number;
    title: string;
}

export interface Role {
    id: number;
    title: string;
    permissions: Permission[];
}

export interface User {
    email: string;
    id?: number;
    avatar?: string;
    first_name?: string;
    last_name?: string;
    fullname?: string;
    is_confirmed?: boolean;
    is_active?: boolean;
    role?: Role;
    teams?: Team[];
    user?: User;
}

export interface LoginResponse {
    token: string;
    user: User;
    email: string;
}

export interface SignupResponse {
    result: string;
    token: string;
    user: User;
    message: string;
}

export interface EmailStatusResponse {
    status: string,
    message: string,
    result: boolean
}

export interface ResetPasswordResponse {
    status: string,
    message: string,
    result: string
}

export interface ConstantsResponse {
    [key: string]: string | [string, string][];
    TITLE_TYPES: [string, string][];
    DOCUMENT_TYPES: [string, string][];
    VEHICLE_STATUSES: [string, string][];
    AUCTION_FIELD_DATA_TYPES: [string, string][];
    AUCTION_FIELD_FILTER_TYPES: [string, string][];
    AUCTION_PROVIDERS: [string, string][];
    AVAILABLE_FIELDS: [string, string][];
    GOOGLE_OAUTH_HREF: string;
    LANGUAGE_CODES: [string, string][];
    OPENAI_MODELS: [string, string][];
    PAYMENT_METHODS: [string, string][];
    QUALIFICATION_RULES_CONDITIONS: [string, string][];
    VEHICLE_BODY_STYLES: [string, string][];
    VEHICLE_DRIVE_TYPES: [string, string][];
    VEHICLE_FEATURES: [string, string][];
    VEHICLE_MARKETS: [string, string][];
    VEHICLE_TRANSMISSIONS: [string, string][];
    WEEKDAYS: [string, string][];
    WHATSAPP_WEBHOOK_KEY: string;
}

// Define actions for auth
export const loginRequest = createAction<{ email: string; password: string }>('auth/loginRequest');
export const loginSuccess = createAction<LoginResponse>('auth/loginSuccess');
export const loginFailure = createAction<string>('auth/loginFailure');

export const deleteRequest = createAction('auth/deleteRequest');
export const deleteSuccess = createAction('auth/deleteSuccess');
export const deleteFailure = createAction<string>('auth/deleteFailure');

export const logoutRequest = createAction('auth/logoutRequest');

export const resetErrorRequest = createAction('auth/resetErrorRequest');

export const loadingOnRequest = createAction('auth/loadingOnRequest');
export const loadingOffRequest = createAction('auth/loadingOffRequest');

export const signupRequest = createAction<{ name: string, email: string; password: string }>('auth/signupRequest');
export const signupSuccess = createAction<SignupResponse>('auth/signupSuccess');
export const signupFailure = createAction<string>('auth/signupFailure');

export const checkEmailRequest = createAction<{ email: string}>('auth/checkEmailRequest');
export const checkEmailSuccess = createAction<EmailStatusResponse>('auth/checkEmailSuccess');
export const checkEmailFailure = createAction<string>('auth/checkEmailFailure');

export const resetPasswordRequest = createAction<{ email: string}>('auth/resetPasswordRequest');
export const resetPasswordSuccess = createAction<boolean>('auth/resetPasswordSuccess');
export const resetPasswordFailure = createAction<string>('auth/resetPasswordFailure');

export const constantsRequest = createAction('auth/constantsRequest');
export const constantsSuccess = createAction<ConstantsResponse>('auth/constantsSuccess');
export const constantsFailure = createAction<string>('auth/constantsFailure');

export const rolesRequest = createAction('auth/rolesRequest');
export const rolesSuccess = createAction<Role[]>('auth/rolesSuccess');
export const rolesFailure = createAction<string>('auth/rolesFailure');

export const profileRequest = createAction('auth/profileRequest');
export const profileSuccess = createAction<User>('auth/profileSuccess');
export const profileFailure = createAction<string>('auth/profileFailure');

export const teamsRequest = createAction('auth/teamsRequest');
export const teamsSuccess = createAction<Team[]>('auth/teamsSuccess');
export const teamsFailure = createAction<string>('auth/teamsFailure');

export const profileUpdateRequest = createAction<User>('auth/profileUpdateRequest');
export const profileUpdateSuccess = createAction<User>('auth/profileUpdateSuccess');
export const profileUpdateFailure = createAction<string>('auth/profileUpdateFailure');